import React, { FunctionComponent, useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ICampaign, ISenderIdentity } from '../../../interfaces/models/campaign';
import CampaignAccountReconnectBanner from '../components/campaigns/CampaignAccountReconnectBanner';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import TooltipWrapper from '../../../components/TooltipWrapper';
import LoadingSpinner from '../../../components/LoadingSpinner';
import CampaignPerformance from '../components/campaigns/performance/CampaignPerformance';
import { IDateRange } from '../components/campaigns/performance/PerformanceFilters';
import { PerformanceMetric } from '../components/campaigns/performance/performanceMetrics';
import CampaignDetails from '../components/campaigns/CampaignDetails';
import CampaignSequence from '../components/campaigns/CampaignSequence';
import { campaignMobile } from '../utils/campaignBreakpoints';
import { IProxyAccountDetails, useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { AppRoutes } from '../../../interfaces/store/appStore';
import CampaignLeadsView from '../components/campaigns/CampaignLeadsView';
import CampaignPanelBoundary from '../components/campaigns/CampaignPanelBoundary';
import { PROXY_UI_POLL_MS, SIM_EVENT } from '../hooks/useCampaignSimulation';

type CampaignPageTab = 'analytics' | 'leads';

const CampaignLeadsPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCampaignById, getSenderIdentities, getProxyAccounts } = useCampaignsRequest();
  const { store: { user } } = useAuthStore();

  const [campaign, setCampaign] = useState<ICampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<CampaignPageTab>('analytics');
  const [senders, setSenders] = useState<ISenderIdentity[]>([]);
  const [sendersLoaded, setSendersLoaded] = useState(false);
  const [proxyAccounts, setProxyAccounts] = useState<Record<string, IProxyAccountDetails>>({});
  // Set by an analytics card's arrow: the Leads tab opens filtered to that card's leads.
  const [leadsActivity, setLeadsActivity] = useState<{ metric: PerformanceMetric; range: IDateRange } | null>(null);

  const autoFetch = !!(location.state as any)?.autoFetch;
  const [highlightStartPanel] = useState(() => !!(location.state as any)?.showStartPanel);

  useEffect(() => {
    if (!id) { navigate(AppRoutes.CAMPAIGNS); return; }
    getCampaignById(id).then(data => {
      if (data?.campaign) {
        const c = autoFetch ? { ...data.campaign, _autoFetch: true } : data.campaign;
        setCampaign(c as ICampaign);
        // Coming from the wizard, or a draft: the start panel lives with the leads.
        if (highlightStartPanel || autoFetch || data.campaign.status === 'draft') setTab('leads');
        // Clear nav state so refresh doesn't re-trigger one-shot flags
        if (autoFetch || highlightStartPanel) navigate('.', { replace: true, state: {} });
      } else {
        navigate(AppRoutes.CAMPAIGNS);
      }
      setLoading(false);
    });
  }, [id]);

  // Keep the header controls (Start/Pause/Resume) in sync with backend changes
  // that happen outside this page (e.g. simulation auto-pausing a campaign).
  useEffect(() => {
    if (!id) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        getCampaignById(id).then(data => {
          if (data?.campaign) setCampaign(prev => prev ? ({ ...prev, ...data.campaign }) as ICampaign : (data.campaign as ICampaign));
        });
      }, 1500);
    };
    window.addEventListener(SIM_EVENT, handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(SIM_EVENT, handler);
    };
  }, [id, getCampaignById]);

  // Proxy: cloud executor / Unipile update MongoDB with no SIM_EVENT — poll campaign meta.
  useEffect(() => {
    if (!id || !campaign) return;
    if (campaign.executionMethod !== 'proxy') return;
    if (campaign.status !== 'active' && campaign.status !== 'paused') return;
    const interval = window.setInterval(() => {
      getCampaignById(id).then(data => {
        if (data?.campaign) {
          setCampaign(prev => prev ? ({ ...prev, ...data.campaign }) as ICampaign : (data.campaign as ICampaign));
        }
      });
    }, PROXY_UI_POLL_MS);
    return () => window.clearInterval(interval);
  }, [id, campaign?.executionMethod, campaign?.status, getCampaignById]);

  // A disconnected LinkedIn account stops the campaign sending — warn above both tabs.
  // Rechecked when the status changes (e.g. resumed after reconnecting).
  useEffect(() => {
    if (!id || campaign?.executionMethod !== 'proxy') return;
    getProxyAccounts(id, 'campaign-page').then((data: any) => {
      const list: IProxyAccountDetails[] = data?.accounts ?? [];
      setProxyAccounts(Object.fromEntries(list.map(a => [a.accountId, a])));
    });
    // getProxyAccounts is rebuilt every render by the request hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, campaign?.executionMethod, campaign?.status]);

  // The campaign's own senders — the analytics filter and the details card list them.
  useEffect(() => {
    if (!id) return;
    getSenderIdentities(false, id, 'campaign-analytics').then((res: any) => {
      const list = res?.data?.identities ?? res?.identities;
      if (Array.isArray(list)) setSenders(list);
      setSendersLoaded(true);
    });
    // getSenderIdentities is rebuilt every render by the request hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <LoadingWrap>
        <LoadingSpinner height="28px" width="28px" />
      </LoadingWrap>
    );
  }

  if (!campaign) return null;

  const isOwner = !!user?._id && String(user._id) === String(campaign.createdBy);
  const editBlocked = !isOwner || campaign.status === 'active' || campaign.status === 'canceled';
  const editTooltip = !isOwner
    ? `Only ${campaign.createdByName || 'the campaign creator'} can edit this campaign.`
    : campaign.status === 'active'
      ? 'Pause the campaign from the campaigns list to edit settings.'
      : campaign.status === 'canceled'
        ? 'Stopped campaigns can’t be edited.'
        : '';

  return (
    <PageWrap>
      <PageHeader>
        <TitleRow>
          <CampaignTitle title={campaign.name}>{campaign.name}</CampaignTitle>
          <TooltipWrapper tooltipText={editTooltip} placement="bottom">
            <span>
              <EditButton
                type="button"
                disabled={editBlocked}
                onClick={() => navigate(AppRoutes.CAMPAIGN_EDIT.replace(':id', campaign._id))}
              >
                <Pencil size={14} strokeWidth={1.75} />
                Edit campaign
              </EditButton>
            </span>
          </TooltipWrapper>
        </TitleRow>
        <BannerSlot>
          <CampaignAccountReconnectBanner campaign={campaign} proxyAccounts={proxyAccounts} />
        </BannerSlot>
        <Tabs role="tablist">
          <Tab type="button" role="tab" aria-selected={tab === 'analytics'} $active={tab === 'analytics'} onClick={() => setTab('analytics')}>
            Campaign analytics
          </Tab>
          <Tab type="button" role="tab" aria-selected={tab === 'leads'} $active={tab === 'leads'} onClick={() => { setLeadsActivity(null); setTab('leads'); }}>
            Leads
          </Tab>
        </Tabs>
      </PageHeader>

      {tab === 'analytics' ? (
        <AnalyticsScroll>
          <CampaignPerformance
            campaigns={[campaign]}
            campaign={campaign}
            identities={senders}
            identitiesLoaded={sendersLoaded}
            currentUserId={user?._id}
            onOpenLeads={(metric, range) => { setLeadsActivity({ metric, range }); setTab('leads'); }}
          />
          <CampaignSequence campaign={campaign} />
          <CampaignDetails campaign={campaign} senders={senders} />
        </AnalyticsScroll>
      ) : (
        <LeadsArea>
          <LeadsCard>
          <CampaignPanelBoundary>
            <CampaignLeadsView
              campaign={campaign}
              fullPage
              hideHeader
              initialActivity={leadsActivity}
              highlightStartPanel={highlightStartPanel}
              onCampaignUpdate={updated => setCampaign(updated)}
              onClose={() => navigate(AppRoutes.CAMPAIGNS)}
            />
          </CampaignPanelBoundary>
          </LeadsCard>
        </LeadsArea>
      )}
    </PageWrap>
  );
};

const PageWrap = styled.div`
  width: 100%; height: 100%; display: flex; flex-direction: column; overflow: hidden;
`;

// The header's own gap spaces the banner; its built-in bottom margin would double it.
const BannerSlot = styled.div`
  &:empty { display: none; }
  & > * { margin-bottom: 0; }
`;

const PageHeader = styled.div`
  display: flex; flex-direction: column; gap: 12px;
  padding: 16px 24px 0;
  flex-shrink: 0;
  ${campaignMobile`padding: 12px 12px 0; gap: 10px;`}
`;

const TitleRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
`;

const CampaignTitle = styled.h1`
  margin: 0; min-width: 0;
  font-family: 'Inter', NunitoSans, sans-serif; font-size: 20px; font-weight: 700; line-height: 28px;
  letter-spacing: -0.44px; color: #111A2D;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  ${campaignMobile`font-size: 18px; line-height: 26px;`}
`;

const EditButton = styled.button`
  display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0;
  height: 34px; padding: 7px 14px;
  border: none; border-radius: 10px;
  background: #6061F8; color: #FCFCFC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  font-family: 'Inter', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  letter-spacing: -0.15px; white-space: nowrap; cursor: pointer;
  &:hover:not(:disabled) { background: #4F50E6; }
  &:disabled { opacity: 0.45; cursor: default; }
`;

const Tabs = styled.div`
  display: flex; gap: 24px;
  border-bottom: 1px solid #E5E7EB;
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  padding: 0 4px 12px;
  border: none; background: transparent; cursor: pointer;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  color: ${({ $active }) => ($active ? '#454DE5' : '#6B7280')};

  &::after {
    content: '';
    position: absolute; left: 0; right: 0; bottom: -1px;
    height: 2px;
    background: ${({ $active }) => ($active ? '#454DE5' : 'transparent')};
  }

  &:hover { color: #454DE5; }
`;

const AnalyticsScroll = styled.div`
  flex: 1; min-height: 0; overflow-y: auto;
  display: flex; flex-direction: column; gap: 12px;
  padding: 12px 24px 32px;
  ${campaignMobile`padding: 12px 12px 24px;`}
`;

// Same frame as the analytics tab, so switching tabs doesn't change the page around it.
const LeadsArea = styled.div`
  flex: 1; min-height: 0; display: flex; flex-direction: column;
  padding: 12px 24px 24px;
  ${campaignMobile`padding: 12px 12px 16px;`}
`;

const LeadsCard = styled.div`
  flex: 1; min-height: 0; display: flex; flex-direction: column;
  background: #FFFFFF;
  border: 1px solid #E9EAEB; border-radius: 12px;
  overflow: hidden;
`;

const LoadingWrap = styled.div`
  display: flex; align-items: center; justify-content: center; height: 100%;
`;

export default CampaignLeadsPage;
